import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Stack, HStack, Button, useToast, Tooltip, Input } from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import Form, { Templates } from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import { eventStatusList, eventTypeList, eventTypeNamesList } from "../../constants/constants";
import { getAllSectionsData } from "../../services/Sections";
import { getAllSubSectionsData } from "../../services/SubSections";
import { transformData, transformOptions } from "../../util/helper";
import ImagePreview from "../../components/ImagePreview";
import { createNewCalendarEvent, getCalendarEventData, updateCalendarEventData } from "../../services/CalendarEvents";
import FormLoader from "../../CommonComponents/FormLoader";
import { useSelector } from "react-redux";
import { handleApiError, showErrorToast, showSuccessToast } from '../../util/errorHandler';
import TypeaheadWidget from "../../CommonComponents/TypeaheadWidget";
import { getResearchCategoriesAPI } from "../../services/ResearchCategories";
import { getUniqueDocTypesFromExcel } from "../../services/DocumentTypes";
import CKEditorWidget from "../../CommonComponents/CKEditorWidget";

// Left Column Schema - Basic Calendar Event Fields
const leftColumnSchema = (alias, schemaState) => ({
    title: "",
    type: "object",
    required: ["alias", "title", "startDate", "endDate"],
    properties: {
        "_id": { type: "string", title: "_id" },
        "isVisible": { type: "boolean", title: "Is Visible" },
        "alias": { type: "string", title: "Alias", default: "" },
        "eventTypeAlias": { type: "string", title: "Event Type Alias", default: "" },
        "projectNumber": { type: ["number", "null"], title: "Project Number", default: null }, 
        "title": { type: "string", title: "Event Title", default: "" },
        "titleDetail": { type: "string", title: "Event Subtitle", default: "" },
        "description": { type: "string", title: "Event Description", default: "" },    
        "startDate": { type: "string", format: "date-time", title: "Event Start Date" },
        "endDate": { type: "string", format: "date-time", title: "Event End Date" },
        "display_time": { type: "string", title: "Display Time", default: "" },
        "display_date": { type: "string", title: "Display Date", default: "" },
        "event_schedule": { type: "string", title: "Time", default: "" }, 
        "eventImage": { type: "string", title: "Event Image", default: "" },
        "preview_eventImage": { "type": "string","title": "Preview Event Image"},
        "eventCardImage": { type: "string", title: "Event Card Image", default: "" },
        "preview_eventCardImage": { "type": "string","title": "Preview Event Card Image"},
        "venue": { type: "string", title: "Venue", default: "" },
        "lat": { type: ["number", "null"], title: "Latitude", default: null },
        "lng": { type: ["number", "null"], title: "Longitude", default: null },
        "mapUrl": { type: "string", title: "Map URL", default: "" },
        "link": { type: "string", title: "Link", default: "" },
        "eventFormat": { type: "string", title: "Event Format", default: "" },
        "association": { type: "string", title: "Association", default: "" },
        "discussantsParticipants_tag": { type: "string", title: "Discussants and Participants", default: "" }
    }
});

// Right Column Schema - Configuration & Status Fields
const rightColumnSchema = (schemaState) => ({
    title: "",
    type: "object",
    properties: {
        isActive: {
            type: "number",
            title: "Is Event Active?",
            enum: [1, 0],
            default: 1
        },
        isHighlight: {
            type: "number",
            title: "Is Event A Session Highlight?",
            enum: [1, 0],
            default: 0
        },
        documentType: schemaState.properties.documentType,
        eventType: {
            type: "string",
            title: "Event Type",
            enum: eventTypeList,
            default: eventTypeList[0]
        },
        section: schemaState.properties.section,
        subSection: schemaState.properties.subSection,
        eventStatus: {
            type: "string",
            title: "Event Status",
            enum: eventStatusList,
            default: eventStatusList[0]
        },
        calendarEventUrl: { type: "string", title: "Calendar URL", default: "" },
        keyword_alias: {
            type: "array",
            title: "Keyword Alias",
            items: { type: "string" },
            default: []
        },
        otherKeywordsAlias: {
            type: "array",
            title: "Other Keywords Alias",
            items: { type: "string" },
            default: []
        },
        research_category_alias: {
            type: "array",
            title: "Research Category Alias",
            items: { type: "string", title: "Research Category Alias" },
            default: []
        },
        "pdfLabel": { type: "string", title: "PDF Label", default: "" },
        "pdfUrl": { type: "string", title: "PDF URL", default: "" }
    }
});

// Left Column UI Schema
const leftColumnUiSchema = (alias, isSubmitting) => ({
    "ui:submitButtonOptions": { "norender": true },
    "ui:style": { fontSize: "14px" },
    "_id": { "ui:widget": "hidden" },
    alias: { "ui:emptyValue": "", "ui:readonly": !!alias },
    projectNumber: { "ui:emptyValue": null }, 
    title: { "ui:emptyValue": "" },
    titleDetail: { "ui:emptyValue": "" },
    eventTypeAlias: { "ui:emptyValue": "" },
    venue: { "ui:emptyValue": "" },
    lat: { "ui:emptyValue": null },
    lng: { "ui:emptyValue": null },
    mapUrl: { "ui:emptyValue": "" },
    link: { "ui:emptyValue": "" },
    description: { "ui:emptyValue": "", "ui:widget": "textarea" },
    startDate: { "ui:emptyValue": "" },
    endDate: { "ui:emptyValue": "" },
    display_time: { "ui:emptyValue": "" },
    display_date: { "ui:emptyValue": "" },
    event_schedule: { "ui:emptyValue": "", "ui:widget": "textarea" },
    eventImage: {
        "ui:emptyValue": "",
        "ui:widget": "eventImageWidget"
    },
    preview_eventImage: { "ui:widget": "hidden" },
    eventCardImage: {
        "ui:emptyValue": "",
        "ui:widget": "eventCardImageWidget"
    },
    preview_eventCardImage: { "ui:widget": "hidden" },
    eventFormat: { "ui:emptyValue": "" },
    association: { "ui:emptyValue": "" },
    discussantsParticipants_tag: {
        "ui:widget": "rte",
        "ui:options": {
            placeholder: "Enter discussants and participants...",
            height: "400px"
        }
    }
});

// Right Column UI Schema
const rightColumnUiSchema = (isSubmitting, eventTypeValue, enumNamesState = {}) => ({
    "ui:submitButtonOptions": { "norender": true },
    "ui:style": { fontSize: "14px" },
    isActive: {
        "ui:widget": "radio",
        "ui:enumNames": ["Yes", "No"],
        "ui:options": { inline: true },
        props: { display: "flex", flexDirection: "row", gap: "10px" },
    },
    isHighlight: {
        "ui:widget": "radio",
        "ui:enumNames": ["Yes", "No"],
        "ui:options": { inline: true },
        props: { display: "flex", flexDirection: "row", gap: "10px" },
    },
    section: { "ui:emptyValue": "", "ui:widget": eventTypeValue === "masterlist" ? "select" : "hidden", "ui:enumNames": enumNamesState.section },
    subSection: { "ui:emptyValue": "", "ui:widget": eventTypeValue === "masterlist" ? "select" : "hidden" },
    calendarEventUrl: {
        "ui:emptyValue": "",
        "ui:widget": "calendarUrl"
    },
    eventStatus: { "ui:widget": "select", "ui:enumNames": eventStatusNamesList },
    eventType: { "ui:widget": "select", "ui:enumNames": eventTypeNamesList },
    documentType: { "ui:widget": "select", "ui:enumNames": enumNamesState.documentType },
    keyword_alias: {
        "ui:options": {
            orderable: true,
            addable: true,
            removable: true
        }
    },
    otherKeywordsAlias: {
        "ui:options": {
            orderable: true,
            addable: true,
            removable: true
        }
    },
    research_category_alias: {
        "ui:options": {
            orderable: true,
            addable: true,
            removable: true
        },
        items: {
            "ui:widget": "researchCategoryTypeahead"
        }
    },
    pdfLabel: { "ui:emptyValue": "" },
    pdfUrl: { "ui:emptyValue": "" }
});

// Combined Schema for Single Column (Preview Mode)
const combinedSchema = (alias, schemaState) => ({
    title: "Calendar Fields",
    type: "object",
    required: ["alias", "title", "startDate", "endDate"],
    properties: {
        "_id": { type: "string", title: "_id" },
        isVisible: {
            type: "boolean",
            title: "Is Visible"
        },
        alias: { type: "string", title: "Alias", default: "" },
        projectNumber: { type: ["number", "null"], title: "Project Number", default: null },
        title: { type: "string", title: "Title", default: "" },
        titleDetail: { type: "string", title: "Title Detail", default: "" },        
        eventTypeAlias: { type: "string", title: "Event Type Alias", default: "" },
        venue: { type: "string", title: "Venue", default: "" },
        lat: { type: ["number", "null"], title: "Latitude", default: null },
        lng: { type: ["number", "null"], title: "Longitude", default: null },
        mapUrl: { type: "string", title: "Map URL", default: "" },
        link: { type: "string", title: "Link", default: "" },
        description: { type: "string", title: "Description", default: "" },
        startDate: { type: "string", format: "date-time", title: "Start Date" },
        endDate: { type: "string", format: "date-time", title: "End Date" },
        display_time: { type: "string", title: "Display Time", default: "" },
        display_date: { type: "string", title: "Display Date", default: "" },
        event_schedule: { type: "string", title: "Time", default: ""  },
        eventImage: { type: "string", title: "Event Image", default: "" },
        preview_eventImage: { "type": "string","title": "Preview Event Image"},

        eventCardImage: { type: "string", title: "Event Card Image", default: "" },
        preview_eventCardImage: { "type": "string","title": "Preview Event Card Image"},

        eventFormat: { type: "string", title: "Event Format", default: "" },
        association: { type: "string", title: "Association", default: "" },
        discussantsParticipants_tag: { type: "string", title: "Discussants and Participants", default: "" },
        eventType: {
            type: "string",
            title: "Event Type",
            enum: eventTypeList,
            default: eventTypeList[0]
        },
        documentType: schemaState.properties.documentType,
        eventStatus: {
            type: "string",
            title: "Event Status",
            enum: eventStatusList,
            default: eventStatusList[0]
        },
        isActive: {
            type: "number",
            title: "Is Event Active?",
            enum: [1, 0],
            default: 1
        },
        isHighlight: {
            type: "number",
            title: "Is Event A Session Highlight?",
            enum: [1, 0],
            default: 0
        },
        section: schemaState.properties.section,
        subSection: schemaState.properties.subSection,
        calendarEventUrl: { type: "string", title: "Calendar Event URL", default: "" },
        keyword_alias: {
            type: "array",
            title: "Keyword Alias",
            items: { type: "string" },
            default: []
        },
        otherKeywordsAlias: {
            type: "array",
            title: "Other Keywords Alias",
            items: { type: "string" },
            default: []
        },
        research_category_alias: {
            type: "array",
            title: "Research Category Alias",
            items: { type: "string", title: "Research Category Alias" },
            default: []
        },
        pdfLabel: { type: "string", title: "PDF Label", default: "" },
        pdfUrl: { type: "string", title: "PDF URL", default: "" }
    }
});

// Combined UI Schema for Single Column (Preview Mode)
const combinedUiSchema = (alias, isSubmitting, eventTypeValue, enumNamesState = {}) => ({
    "ui:submitButtonOptions": { "norender": true },
    "ui:style": { fontSize: "14px" },
    "_id": { "ui:widget": "hidden" },
    alias: { "ui:emptyValue": "", "ui:readonly": !!alias },
    projectNumber: { "ui:emptyValue": null },
    title: { "ui:emptyValue": "" },
    titleDetail: { "ui:emptyValue": "" },
    eventTypeAlias: { "ui:emptyValue": "", "ui:readonly": !!alias },
    venue: { "ui:emptyValue": "" },
    lat: { "ui:emptyValue": null },
    lng: { "ui:emptyValue": null },
    mapUrl: { "ui:emptyValue": "" },
    link: { "ui:emptyValue": "" },
    description: { "ui:emptyValue": "", "ui:widget": "textarea" },
    startDate: { "ui:emptyValue": "" },
    endDate: { "ui:emptyValue": "" },
    display_time: { "ui:emptyValue": "" },
    display_date: { "ui:emptyValue": "" },
    event_schedule: { "ui:emptyValue": "", "ui:widget": "textarea" },
    eventImage: {
        "ui:emptyValue": "",
        "ui:widget": "eventImageWidget"
    },
    preview_eventImage: { "ui:widget": "hidden" },

    eventCardImage: {
        "ui:emptyValue": "",
        "ui:widget": "eventCardImageWidget"
    },
    preview_eventCardImage: { "ui:widget": "hidden" },

    eventFormat: { "ui:emptyValue": "" },
    association: { "ui:emptyValue": "" },
    discussantsParticipants_tag: {
        "ui:widget": "rte",
        "ui:options": {
            placeholder: "Enter discussants and participants...",
            height: "400px"
        }
    },
    isActive: {
        "ui:widget": "radio",
        "ui:enumNames": ["Yes", "No"],
        "ui:options": { inline: true },
        props: { display: "flex", flexDirection: "row", gap: "10px" },
    },
    isHighlight: {
        "ui:widget": "radio",
        "ui:enumNames": ["Yes", "No"],
        "ui:options": { inline: true },
        props: { display: "flex", flexDirection: "row", gap: "10px" },
    },
    section: { "ui:emptyValue": "", "ui:widget": eventTypeValue === "masterlist" ? "select" : "hidden", "ui:enumNames": enumNamesState.section },
    subSection: { "ui:emptyValue": "", "ui:widget": eventTypeValue === "masterlist" ? "select" : "hidden" },
    calendarEventUrl: {
        "ui:emptyValue": "",
        "ui:widget": "calendarUrl"
    },
    eventStatus: { "ui:widget": "select", "ui:enumNames": eventStatusNamesList },
    eventType: { "ui:widget": "select", "ui:enumNames": eventTypeNamesList },
    documentType: { "ui:widget": "select", "ui:enumNames": enumNamesState.documentType },
    keyword_alias: {
        "ui:options": {
            orderable: true,
            addable: true,
            removable: true
        }
    },
    otherKeywordsAlias: {
        "ui:options": {
            orderable: true,
            addable: true,
            removable: true
        }
    },
    research_category_alias: {
        "ui:options": {
            orderable: true,
            addable: true,
            removable: true
        },
        items: {
            "ui:widget": "researchCategoryTypeahead"
        }
    },
    pdfLabel: { "ui:emptyValue": "" },
    pdfUrl: { "ui:emptyValue": "" }
});


const eventStatusNamesList = eventStatusList.map(
    status => status.charAt(0).toUpperCase() + status.slice(1)
);

const schemaTemplate = {
    title: "Calendar Fields",
    type: "object",
    required: ["alias", "title"],
    properties: {
        alias: { type: "string", title: "Alias", default: "" },
        projectNumber: { type: ["number", "null"], title: "Project Number", default: null },
        title: { type: "string", title: "Title", default: "" },
        titleDetail: { type: "string", title: "Title Detail", default: "" },
        eventTypeAlias: { type: "string", title: "Event Type Alias", default: "" },
        venue: { type: "string", title: "Venue", default: "" },
        lat: { type: ["number", "null"], title: "Latitude", default: null },
        lng: { type: ["number", "null"], title: "Longitude", default: null },
        mapUrl: { type: "string", title: "Map URL", default: "" },
        link: { type: "string", title: "Link", default: "" },
        description: { type: "string", title: "Description", default: "" },
        startDate: { type: "string", format: "date-time", title: "Start Date" },
        endDate: { type: "string", format: "date-time", title: "End Date" },
        display_time: { type: "string", format: "time", title: "Display Time", default: "" },
        display_date: { type: "string", format: "date", title: "Display Date", default: "" },
        event_schedule: { type: "string", title: "Time", default: ""  },
        eventType: { type: "string", title: "Event Type", enum: eventTypeList, default: eventTypeList[0] },
        documentType: { type: "string", title: "Document Type", enum: [""], default: "" },
        eventStatus: { type: "string", title: "Event Status", enum: eventStatusList, default: eventStatusList[0] },
        isActive: { type: "number", title: "Is Event Active?", enum: [1, 0] },
        isHighlight: { type: "number", title: "Is Event A Session Highlight?", enum: [1, 0] },
        isVisible: { type: "boolean", title: "Is Visible", default: false },
        section: { type: "string", title: "Section", enum: [""] },
        subSection: { type: "string", title: "Sub Section", enum: [""] },
        calendarEventUrl: { type: "string", title: "Calendar Event URL", default: "" },
        eventImage: { type: "string", title: "Event Image", default: "" },
        preview_eventImage: { "type": "string","title": "Preview Event Image"},
        eventCardImage: { type: "string", title: "Event Card Image", default: "" },
        preview_eventCardImage: { "type": "string","title": "Preview Event Card Image"},
        eventFormat: { type: "string", title: "Event Format", default: "" },
        association: { type: "string", title: "Association", default: "" },
        discussantsParticipants_tag: { type: "string", title: "Discussants and Participants", default: "" },
        pdfLabel: { type: "string", title: "PDF Label", default: "" },
        pdfUrl: { type: "string", title: "PDF URL", default: "" }
    },
};

const Section1 = () => {
    const { alias } = useParams();
    const [leftFormData, setLeftFormData] = useState({});
    const [rightFormData, setRightFormData] = useState({});
    const [combinedFormData, setCombinedFormData] = useState({});
    const [schemaState, setSchemaState] = useState(schemaTemplate);
    const [enumNamesState, setEnumNamesState] = useState({ section: [], documentType: [], subSection: [] });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [quickSave, setQuickSave] = useState(false);
    const previewEnabled = useSelector(state => state.preview.isEnabled);
    const [key, setKey] = useState(0); // For forcing re-renders
    const toast = useToast();
    const navigate = useNavigate();
    const originalGeoRef = useRef({});

    // Custom widget for event image with preview icon
    const EventImageWidget = useCallback((props) => {
        const { id, value, onChange, placeholder, label, required } = props;
        return (
            <Box>
                {label ? (
                    <Box as="label" htmlFor={id} display="block" mb={2} fontWeight="500">
                        {label}{required ? " *" : ""}
                    </Box>
                ) : null}
                <Input
                    id={id}
                    type="text"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder || "Enter image URL"}
                />
                {value ? (
                    <Box mt={3}>
                        <ImagePreview image={value} variant="thumbnail" />
                    </Box>
                ) : null}
            </Box>
        );
    }, []);

    // Tooltip message for Calendar URL
    const calendarUrlTooltipMessage = useMemo(() => (
        <Box>
            <Box mb={2}>
                <strong>• Internal page: Enter only the page path (no domain).</strong>
                <Box mt={1} fontSize="sm">
                    Example:
                    <Box as="code" display="block" mt={1} p={1} bg="gray.100" borderRadius="md">
                       event-film-festival/9th-osians-cinefan-festival-of-asian-cinema/agraphy
                    </Box>
                </Box>
                <strong>• External website: Enter the full URL starting with https://.</strong>
                 <Box mt={1} fontSize="sm">
                    Example:
                    <Box as="code" display="block" mt={1} p={1} bg="gray.100" borderRadius="md">
                       Example: https://www.google.com
                       External links will open in a new tab.
                    </Box>
                </Box>
            </Box>
        </Box>
    ), []);

    // Custom field template for Calendar URL with tooltip on label
    const CalendarUrlFieldTemplate = useCallback((props) => {
        const { id, classNames, style, label, help, description, errors, children, schema } = props;
        
        // Only apply custom template for calendarEventUrl field
        // Check by field id (contains 'calendarEventUrl') or schema title
        const isCalendarUrlField = id?.includes('calendarEventUrl') || 
                                   schema?.title === "Calendar URL" || 
                                   schema?.title === "Calendar Event URL";
        
        if (isCalendarUrlField) {
            return (
                <Box className={classNames} style={style}>
                    <HStack spacing={1} alignItems="center" mb={1}>
                        <Box as="label" htmlFor={id} fontWeight="medium">
                            {label}
                        </Box>
                        <Tooltip 
                            label={calendarUrlTooltipMessage} 
                            placement="top" 
                            hasArrow
                            bg="white"
                            color="black"
                            p={4}
                            borderRadius="md"
                            boxShadow="lg"
                            maxW="500px"
                            fontSize="sm"
                        >
                            <Box as="span" display="inline-flex" alignItems="center" cursor="help">
                                <InfoIcon boxSize={4} color="blue.500" />
                            </Box>
                        </Tooltip>
                    </HStack>
                    {description && <Box fontSize="sm" color="gray.600" mb={2}>{description}</Box>}
                    {children}
                    {errors && <Box color="red.500" fontSize="sm" mt={1}>{errors}</Box>}
                    {help && <Box fontSize="sm" color="gray.600" mt={1}>{help}</Box>}
                </Box>
            );
        }
        
        // Use default template for other fields to avoid duplication
        const DefaultFieldTemplate = Templates.FieldTemplate;
        return <DefaultFieldTemplate {...props} />;
    }, [calendarUrlTooltipMessage]);

    // Custom widget for Calendar URL (simplified, just the input)
    const CalendarUrlWidget = useCallback((props) => {
        const { value, onChange } = props;
        
        return (
            <Input
                type="text"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Enter calendar event URL"
            />
        );
    }, []);

    // Split data for two-column layout
    const splitFormData = (data = {}) => {
        const leftFields = [
            '_id',
            'isVisible',
            'alias',
            'projectNumber',
            'title',
            'titleDetail',
            'eventTypeAlias',
            'venue',
            'lat',
            'lng',
            'mapUrl',
            'link',
            'description',
            'startDate',
            'endDate',
            'display_time',
            'display_date',
            'event_schedule',
            'eventImage',
            'preview_eventImage',
            'eventCardImage',
            'preview_eventCardImage',
            'eventFormat',
            'association',
            'discussantsParticipants_tag'           
        ];
        const rightFields = [
            'documentType',
            'eventType',
            'eventStatus',
            'isActive',
            'isHighlight',
            'section',
            'subSection',
            'calendarEventUrl',
            'keyword_alias',
            'otherKeywordsAlias',
            'research_category_alias',
            'pdfLabel',
            'pdfUrl'
        ];
        
        const leftData = {};
        const rightData = {};
        
        // Process left fields with defaults
        leftFields.forEach(field => {
            if (data.hasOwnProperty(field)) {
                leftData[field] = data[field];
                // if (field === 'projectNumber') {
                //  leftData[field] = data[field] !== null && data[field] !== undefined ? Number(data[field]) : null;
                // }
                 if (field === 'discussantsParticipants_tag') {
                    // Convert to string and handle null/undefined/empty
                    const value = data[field];
                    leftData[field] = (value === null || value === undefined) ? "" : String(value);
                 }
            } else {
                if (field === 'isVisible') leftData[field] = false;
                else if (field === 'lat' || field === 'lng') leftData[field] = null;
                else leftData[field] = "";
            }
        });
        
        // Process right fields with defaults
        rightFields.forEach(field => {
            if (data.hasOwnProperty(field)) {
                rightData[field] = data[field];
            } else {
                // Set appropriate defaults
                if (field === 'isActive') rightData[field] = 1;
                else if (field === 'isHighlight') rightData[field] = 0;
                else if (field === 'eventType') rightData[field] = eventTypeList[0];
                else if (field === 'eventStatus') rightData[field] = eventStatusList[0];
                else if (field === 'documentType') rightData[field] = (schemaState?.properties?.documentType?.enum?.[0] ?? "");
                else if (field === 'keyword_alias') rightData[field] = [];
                else if (field === 'otherKeywordsAlias') rightData[field] = [];
                else if (field === 'research_category_alias') rightData[field] = [];
                else rightData[field] = "";
            }
        });
        
        return { leftData, rightData };
    };
    
    // Combine data from both forms
    const combineFormData = (leftData, rightData) => {
        return { ...leftData, ...rightData };
    };

    // Handle form changes for two-column layout
    const handleLeftFormChange = (data) => {
        const formData = { ...data.formData };
        if (formData.projectNumber === "" || formData.projectNumber === undefined) {
            formData.projectNumber = null;
        }
        if (formData.lat === "" || formData.lat === undefined) formData.lat = null;
        if (formData.lng === "" || formData.lng === undefined) formData.lng = null;
        setLeftFormData(formData);
    };
    
    const handleRightFormChange = (data) => {
        const next = { ...data.formData };
        // Reset sections if eventType is not masterlist
        if (next.eventType !== rightFormData.eventType && next.eventType !== "masterlist") {
            next.section = "";
            next.subSection = "";
        }
        // Handle section change for subsections only when eventType is masterlist
        if (next.eventType === "masterlist" && next.section !== rightFormData.section) {
            fetchSubSections(next.section, false);
        }
        setRightFormData(next);
    };

    const handleCombinedFormChange = (data) => {
        const next = { ...data.formData };
        if (next.projectNumber === "" || next.projectNumber === undefined) {
            next.projectNumber = null;
        }
        if (next.lat === "" || next.lat === undefined) next.lat = null;
        if (next.lng === "" || next.lng === undefined) next.lng = null;
        // Reset sections if eventType is not masterlist
        if (next.eventType !== combinedFormData.eventType && next.eventType !== "masterlist") {
            next.section = "";
            next.subSection = "";
        }
        // Handle section change for subsections only when eventType is masterlist
        if (next.eventType === "masterlist" && next.section !== combinedFormData.section) {
            fetchSubSections(next.section, false);
        }
        setCombinedFormData(next);
    };

    // Fetch research categories for typeahead
    const fetchResearchCategories = useCallback(async (inputValue) => {
        try {
            const result = await getResearchCategoriesAPI();
            const transformedOptions = transformOptions(result);
            const search = (inputValue || '').toLowerCase();
            return transformedOptions?.filter((option) =>
                option.label?.toLowerCase().includes(search)
            );
        } catch (e) {
            return [];
        }
    }, []);

    // Widgets object
    const widgets = useMemo(() => ({
        eventImageWidget: EventImageWidget,
        eventCardImageWidget: EventImageWidget,
        calendarUrl: CalendarUrlWidget,
        rte: CKEditorWidget,
        researchCategoryTypeahead: (props) => (
            <TypeaheadWidget {...props} fetchOptions={fetchResearchCategories} />
        )
    }), [EventImageWidget, CalendarUrlWidget, fetchResearchCategories]);

    // Custom templates for RJSF
    const customTemplates = useMemo(() => ({
        ...Templates,
        FieldTemplate: CalendarUrlFieldTemplate
    }), [CalendarUrlFieldTemplate]);

    // Transform errors to remove projectNumber validation errors
    const transformErrors = useCallback((errors) => {
        return errors.filter(error => {
            if (error.property === '.projectNumber') return false;
            if (error.property === '.lat') return false;
            if (error.property === '.lng') return false;
            return true;
        });
    }, []);

    // Fetch sections and set schema
    useEffect(() => {
        const fetchSections = async () => {
            try {
                const result = await getAllSectionsData();

                if (result?.data?.data?.length) {
                    // 1. Extract array of strings
                    const rawSections = result?.data?.data;

                    // 2. Deduplicate and sort
                    const uniqueSections = [...new Set(rawSections)].sort();

                    // 3. Add a blank option at the beginning
                    const finalSections = ["", ...uniqueSections];

                    // 4. Update schema
                    setSchemaState(prevSchema => ({
                        ...prevSchema,
                        properties: {
                            ...prevSchema.properties,
                            section: {
                                ...prevSchema.properties.section,
                                enum: finalSections,
                            },
                        },
                    }));
                    setEnumNamesState(prev => ({
                        ...prev,
                        section: finalSections.map(item =>
                            item === "" ? "Select a section" : item.replace(/-/g, " ").toUpperCase()
                        ),
                    }));

                    setKey(prevKey => prevKey + 1); // Force re-render
                }
            } catch (error) {
                handleApiError(error, toast, {
                    defaultErrorMessage: "Failed to fetch sections",
                });
            }
        };

        const fetchDocumentTypes = async () => {
            try {
                const list = await getUniqueDocTypesFromExcel();
                if (Array.isArray(list) && list.length > 0) {
                    // Remove duplicates from enum values and names
                    const allValues = ["", ...list.map(item => item?.Document_Type || "").filter(Boolean)];
                    const allNames = ["Select a document type", ...list.map(item => item?.Document_Type || item?.Document_Type_in_Full || "").filter(Boolean)];
                    
                    const enumValues = [...new Set(allValues)];
                    const enumNames = [...new Set(allNames)];

                    setSchemaState(prevSchema => ({
                        ...prevSchema,
                        properties: {
                            ...prevSchema.properties,
                            documentType: {
                                ...prevSchema.properties.documentType,
                                enum: enumValues,
                                default: enumValues[0] || ""
                            }
                        }
                    }));
                    setEnumNamesState(prev => ({ ...prev, documentType: enumNames }));
                    setKey(prevKey => prevKey + 1); // Force re-render
                }
            } catch (error) {
                handleApiError(error, toast, {
                    defaultErrorMessage: "Failed to fetch document types",
                });
            }
        };

        fetchSections();
        fetchDocumentTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Convert an ISO string (any offset) to a datetime-local string in IST (Asia/Kolkata).
    // Always renders as IST wall clock time: "YYYY-MM-DDTHH:mm"
    // NOTE: This function is currently not used as we keep dates in ISO format for @rjsf
    // Keeping it commented for potential future use
    // const convertISOToLocalDateTime = (isoString) => {
    //     if (!isoString) return "";
    //     try {
    //         const timestamp = Date.parse(isoString);
    //         if (Number.isNaN(timestamp)) return "";
    //         // Add +05:30 to get IST wall time, then read with UTC getters to avoid host TZ
    //         const IST_OFFSET_MINUTES = 330;
    //         const istDate = new Date(timestamp + IST_OFFSET_MINUTES * 60 * 1000);
    //         const pad = (n) => String(n).padStart(2, "0");
    //         const year = istDate.getUTCFullYear();
    //         const month = pad(istDate.getUTCMonth() + 1);
    //         const day = pad(istDate.getUTCDate());
    //         const hours = pad(istDate.getUTCHours());
    //         const minutes = pad(istDate.getUTCMinutes());
    //         return `${year}-${month}-${day}T${hours}:${minutes}`;
    //     } catch {
    //         return "";
    //     }
    // };

    // Convert user-entered value to ISO string annotated as IST (+05:30).
    // Supports both:
    // 1) datetime-local: "YYYY-MM-DDTHH:mm"  -> treat as IST wall time
    // 2) ISO with zone:  "YYYY-MM-DDTHH:mm:ss(.sss)?(Z|±HH:MM)" -> convert instant to IST wall time
    const convertLocalDateTimeToISO = (localDateTimeString) => {
        if (!localDateTimeString) return "";
        try {
            const pad = (n) => String(n).padStart(2, "0");
            const IST_OFFSET_MINUTES = 330;
            const hasZoneInfo = /Z|[+-]\d{2}:\d{2}$/.test(localDateTimeString);

            if (hasZoneInfo) {
                // Case 2: Input already has zone (e.g., widget emitted UTC "Z")
                const ts = Date.parse(localDateTimeString);
                if (Number.isNaN(ts)) return "";
                // Shift to IST wall time then format with +05:30
                const ist = new Date(ts + IST_OFFSET_MINUTES * 60 * 1000);
                const y = ist.getUTCFullYear();
                const m = pad(ist.getUTCMonth() + 1);
                const d = pad(ist.getUTCDate());
                const hh = pad(ist.getUTCHours());
                const mm = pad(ist.getUTCMinutes());
                return `${y}-${m}-${d}T${hh}:${mm}:00+05:30`;
            }


            // Case 1: Plain datetime-local without zone
            const [datePart, timePart] = localDateTimeString.split("T");
            if (!datePart || !timePart) return "";
            const [y, m, d] = datePart.split("-").map((v) => Number(v));
            const [hh, mm] = timePart.split(":").map((v) => Number(v));
            if ([y, m, d, hh, mm].some((v) => Number.isNaN(v))) return "";
            return `${pad(y)}-${pad(m)}-${pad(d)}T${pad(hh)}:${pad(mm)}:00+05:30`;
        } catch {
            return "";
        }
    };

    // Fetch initial calendar event data
    useEffect(() => {
        const getBasicData = async () => {
            try {
                if (alias) {
                    const result = await getCalendarEventData(alias);
                    if (result?.data?.[0]) {
                        const transformedData = transformData(result.data[0], []);

                        // Flatten nested geo object into top-level fields for the form
                        if (result.data[0]?.geo && typeof result.data[0].geo === 'object') {
                            const geo = result.data[0].geo;
                            originalGeoRef.current = geo;
                            transformedData.lat = geo.lat ?? null;
                            transformedData.lng = geo.lng ?? null;
                            transformedData.mapUrl = geo.mapUrl ?? "";
                        }

                        // // Keep dates in ISO format - @rjsf datetime widget expects ISO format
                        // // The widget will handle display conversion automatically
                        // if (transformedData.startDate) {
                        //     transformedData.startDate = convertISOToLocalDateTime(transformedData.startDate);
                        // }
                        // if (transformedData.endDate) {
                        //     transformedData.endDate = convertISOToLocalDateTime(transformedData.endDate);
                        // }

                        // Split data for two-column layout
                        const { leftData, rightData } = splitFormData(transformedData);
                        setLeftFormData(leftData);
                        setRightFormData(rightData);
                        setCombinedFormData(transformedData);

                        // Fetch subsections for the initial section
                        if (transformedData?.section) {
                            fetchSubSections(transformedData.section, true);
                        }
                    }
                } else {
                    // For new calendar events, initialize empty form data with projectNumber default
                    const emptyData = { projectNumber: null };
                    const { leftData, rightData } = splitFormData(emptyData);
                    setLeftFormData(leftData);
                    setRightFormData(rightData);
                    setCombinedFormData(emptyData);
                }
            } catch (error) {
                handleApiError(error, toast, {
                    defaultErrorMessage: "Failed to fetch calendar event data"
                });
            } finally {
                setIsLoading(false);
            }
        };

        getBasicData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alias, quickSave]);

    // Fetch and update subsections dynamically
    const fetchSubSections = async (selectedSection, isInitialLoad = false) => {
        try {
            const result = await getAllSubSectionsData(selectedSection);
            if (result?.data) {
                setSchemaState((prevSchema) => ({
                    ...prevSchema,
                    properties: {
                        ...prevSchema.properties,
                        subSection: {
                            ...prevSchema.properties.subSection,
                            enum: result.data || [""],
                            default: isInitialLoad ? undefined : result.data[0], // Default only if not initial load
                        },
                    },
                }));

                if (!isInitialLoad) {
                    // Update form data states
                    setRightFormData((prevData) => ({
                        ...prevData,
                        subSection: result.data[0] || "",
                    }));
                    setCombinedFormData((prevData) => ({
                        ...prevData,
                        subSection: result.data[0] || "",
                    }));
                }

                setKey((prevKey) => prevKey + 1); // Force re-render
            }
        } catch (error) {
            handleApiError(error, toast, {
                defaultErrorMessage: "Failed to fetch sub sections"
            });
        }
    };


    const handleSubmit = async (formData = null) => {
        // Use combined form data in preview mode, otherwise combine left and right forms
        const finalFormData = previewEnabled ? combinedFormData : combineFormData(leftFormData, rightFormData);
        
        if (finalFormData.projectNumber === "" || finalFormData.projectNumber === undefined) {
            finalFormData.projectNumber = null;
        }
        if (finalFormData.lat === "" || finalFormData.lat === undefined) finalFormData.lat = null;
        if (finalFormData.lng === "" || finalFormData.lng === undefined) finalFormData.lng = null;

        // Re-nest geo fields back into the geo object for the API
        finalFormData.geo = {
            ...originalGeoRef.current,
            lat: finalFormData.lat,
            lng: finalFormData.lng,
            mapUrl: finalFormData.mapUrl ?? "",
        };
        delete finalFormData.lat;
        delete finalFormData.lng;
        delete finalFormData.mapUrl;

        // Ensure discussantsParticipants_tag is always present and is a string (RTE widget needs it)
        if (finalFormData.discussantsParticipants_tag === undefined || finalFormData.discussantsParticipants_tag === null) {
            finalFormData.discussantsParticipants_tag = "";
        } else {
            // Ensure it's always a string
            finalFormData.discussantsParticipants_tag = String(finalFormData.discussantsParticipants_tag);
        }
        
        // Convert datetime-local format back to ISO format for API
        if (finalFormData.startDate) {
            finalFormData.startDate = convertLocalDateTimeToISO(finalFormData.startDate);
        }
        if (finalFormData.endDate) {
            finalFormData.endDate = convertLocalDateTimeToISO(finalFormData.endDate);
        }
           if (finalFormData.startDate && finalFormData.endDate) {
            const startDate = new Date(finalFormData.startDate);
            const endDate = new Date(finalFormData.endDate);
            
            if (endDate < startDate) {
                showErrorToast(toast, "End Date cannot be earlier than Start Date", "top-right");
                return;
            }
        }

        try {
            setIsSubmitting(true)
            const result = alias
                ? await updateCalendarEventData(alias, finalFormData)
                : await createNewCalendarEvent(finalFormData);

            if (result?.status === 200 || result?.status === 201) {
                setQuickSave(prev => !prev);
                showSuccessToast(toast, alias
                    ? "Data updated successfully"
                    : "New calendar event created successfully");

                if (!alias) navigate(`/calendar/events/${result?.data?.alias}`);
            }
        } catch (error) {
            handleApiError(error, toast);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box maxW="auto">
            {isLoading ? (
                <FormLoader />
            ) : (
                <Box>
                    {previewEnabled ? (
                        /* Single column layout for preview mode */
                <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
                            <Box flex={1}>
                            <Form
                                key={key} // Force re-render when schema updates
                                    schema={combinedSchema(alias, schemaState)}
                                    uiSchema={combinedUiSchema(alias, isSubmitting, combinedFormData?.eventType, enumNamesState)}
                                    formData={combinedFormData}
                                    validator={validator}
                                    onChange={handleCombinedFormChange}
                                    onSubmit={(e) => {
                                        // RJSF validates before calling onSubmit
                                        // Only proceed if no validation errors
                                        if (!e.errors || e.errors.length === 0) {
                                            handleSubmit();
                                        }
                                    }}
                                    transformErrors={transformErrors}
                                    widgets={widgets}
                                    templates={customTemplates}
                                    fontsize={"14px"}
                                />
                            </Box>
                        </Box>
                    ) : (
                        /* Two column layout with separate forms */
                        <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
                            <Stack flexDirection="row" gap={4} alignItems="flex-start" flex={1}>
                                <HStack flex={1}>
                                    <Box width="100%" bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                                        <Box mb={4} fontWeight="bold" color="blue.800">Column 1 - Calendar Fields</Box>
                                        <Form
                                            key={key} // Force re-render when schema updates
                                            schema={leftColumnSchema(alias, schemaState)}
                                            uiSchema={leftColumnUiSchema(alias, isSubmitting)}
                                            formData={leftFormData}
                                            validator={validator}
                                            onChange={handleLeftFormChange}
                                            onSubmit={(e) => {
                                                // RJSF validates before calling onSubmit
                                                // Only proceed if no validation errors
                                                if (!e.errors || e.errors.length === 0) {
                                                    handleSubmit();
                                                }
                                            }}
                                            transformErrors={transformErrors}
                                            widgets={widgets}
                                            templates={customTemplates}
                                            fontsize={"14px"}
                                        />
                                    </Box>
                                </HStack>
                                <HStack flex={1}>
                                    <Box width="100%" bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                                        <Box mb={4} fontWeight="bold" color="blue.800">Column 2 - Calendar Fields</Box>
                                        <Form
                                            key={key} // Force re-render when schema updates
                                            schema={rightColumnSchema(schemaState)}
                                            uiSchema={rightColumnUiSchema(isSubmitting, rightFormData?.eventType, enumNamesState)}
                                            formData={rightFormData}
                                            validator={validator}
                                            onChange={handleRightFormChange}
                                            widgets={widgets}
                                            templates={customTemplates}
                                            fontsize={"14px"}
                            />
                        </Box>
                    </HStack>
                            </Stack>
                        </Box>
                    )}
                </Box>
            )}
            
            {/* Fixed Save Button at Bottom */}
            <Box
                position="fixed"
                bottom={0}
                left="250px" // Account for sidebar width
                right={0}
                bg="white"
                borderTop="1px solid"
                borderColor="gray.200"
                p={3}
                zIndex={1200}
                display="flex"
            >
                <Box maxW="auto" display="flex" mx="auto">
                    <Button
                        colorScheme="blue"
                        size="lg"
                        width="auto"
                        isLoading={isSubmitting}
                        loadingText="Saving..."
                        onClick={() => {
                            document.querySelectorAll('form').forEach(f => {
                                const b = document.createElement('button');
                                b.type = 'submit';
                                b.hidden = true;
                                f.appendChild(b);
                                b.click();
                                f.removeChild(b);
                            });
                        }}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Box>
            
            {/* Add padding to prevent content from being hidden behind fixed button */}
            <Box pb="100px" />
        </Box>
    );
};

export default Section1;